import {
  useGetAllWishlistItemsByUserQuery,
  useGetAllWishlistsQuery,
  useIsTravelApp,
  wishlistsApi,
} from "@capone/common";
import { STAYS_WISHLIST } from "@capone/experiments";
import { Box, Divider, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import {
  AmenityIcon,
  CategorizedAmenities,
  CloseButtonIcon,
  GenericInfoPopup,
  getUserRatingQualifier,
  HomeSummary,
  HotelDescription,
  Icon,
  IconName,
  PageTabNavigations,
  pluralize,
  ReviewIconTooltip,
  ReviewScore,
  useDeviceTypes,
  VacationRentalReviewsSection,
  VRMap,
  RoomsAndBeds,
} from "halifax";
import H from "history";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useSelector } from "react-redux";
import { RouteComponentProps } from "react-router";
import {
  ListingCollectionEnum,
  MODAL_ALERT,
  ModalCategoryType,
  ModalScreens,
  VIEWED_VR_DETAILS,
} from "redmond";
import {
  Amenity,
  ListingAddressEnum,
  ListingSearchResult,
} from "redmond/apis/tysons/vacation-rentals";
import { DesktopHotelPhotoModal } from "../../../shop/components/DesktopHotelPhotoModal";
import clsx from "clsx";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { VacationRentalsAlertProperties } from "../../../book/components/VRBookingErrorModal/component";
import { VRHouseRules } from "../VRHouseRules";
import { ConnectedDesktopRewardsHeader } from "../../../rewards/components/DesktopRewardsHeader/container";
import { ConnectedRewardsAccountSelection } from "../../../rewards/components/RewardsAccountSelection/container";
import { PremierCollectionShopDetailsSkeleton, HotelCarousel } from "halifax";
import { DesktopListingSummaryBox } from "../DesktopListingSummaryBox";
import { MobileListingTotalPanel } from "../MobileListingTotalPanel";
import {
  ConnectedWishlistManagementModal,
  WishlistSnackbar,
} from "../../../wishlist";
import * as constants from "./constants";
import { VacationRentalShopConnectorProps } from "./container";
import "./styles.scss";
import { goToVRAvailability } from "../../utils/queryStringHelpers";
import { MobileShopHeader } from "../../../shop/components";

export interface IShopProps
  extends VacationRentalShopConnectorProps,
    RouteComponentProps {
  isVRForPremiumCardHoldersEnabled: boolean;
  largestValueAccount?: any;
}

export const VRShop = (props: IShopProps) => {
  const {
    history,
    selectedListing,
    fetchedVacationRentalDetails,
    viewedVacationRentalDetailsProperties,
    fromDate,
    untilDate,
    isVRForPremiumCardHoldersEnabled,
    adultsCount,
    childrenCount,
    petsCount,
    isTravelApp,
    setIsTravelApp,
    largestValueAccount,
  } = props;

  const expState = useExperiments();
  const isWishlistExperiment =
    getExperimentVariant(expState.experiments, STAYS_WISHLIST) === AVAILABLE;

  useGetAllWishlistItemsByUserQuery(undefined, {
    skip: !isWishlistExperiment,
  });
  useGetAllWishlistsQuery(undefined, {
    skip: !isWishlistExperiment,
  });

  const isXmodRedesign = getExperimentVariant(
    expState.experiments,
    XMOD_V1_EXPERIMENT
  ).startsWith(AVAILABLE);

  const travelApp = useIsTravelApp();

  useEffect(() => {
    setIsTravelApp(travelApp.isTravelApp);
  }, [travelApp.isTravelApp]);

  const {
    data: favoritedLodgings,
    isSuccess: isFavoritedLodgingsSuccess,
    isError: isFavoritedLodgingsError,
  } = useSelector(wishlistsApi.endpoints.getAllWishlistItemsByUser.select());

  const isWishlistsLoaded =
    isFavoritedLodgingsSuccess || isFavoritedLodgingsError;

  const isFavorited = favoritedLodgings?.has(
    selectedListing?.listingId.id || ""
  );

  useEffect(() => {
    if (isWishlistExperiment && !isWishlistsLoaded) {
      return;
    }
    if (fetchedVacationRentalDetails) {
      trackEvent({
        eventName: VIEWED_VR_DETAILS,
        ...viewedVacationRentalDetailsProperties,
        properties: {
          ...viewedVacationRentalDetailsProperties.properties,
          ...(isWishlistExperiment && {
            is_favorited: isFavorited,
          }),
        },
      });
    }
  }, [fetchedVacationRentalDetails, isWishlistExperiment, isWishlistsLoaded]);

  useEffect(() => {
    // removes medallia from the fixed bottom that was covering pricing
    if (document && document.getElementById("nebula_div_btn")) {
      document!.getElementById("nebula_div_btn")!.style.display = "none";
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [document.getElementById("nebula_div_btn")]);

  if (!fetchedVacationRentalDetails) {
    return <PremierCollectionShopDetailsSkeleton />;
  }

  if (!selectedListing) {
    return (
      <ShopErrorModal
        history={history}
        fromDate={fromDate}
        untilDate={untilDate}
        adultsCount={adultsCount}
        childrenCount={childrenCount}
        petsCount={petsCount}
      />
    );
  }

  return (
    <ShopListingDetails
      selectedListing={selectedListing}
      history={history}
      isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
      isTravelApp={isTravelApp}
      isXmodRedesign={isXmodRedesign}
      largestValueAccount={largestValueAccount}
    />
  );
};

interface ShopErrorModalProps {
  history: H.History;
  selectedListing?: ListingSearchResult;
  fromDate?: Date | null;
  untilDate?: Date | null;
  adultsCount: number;
  childrenCount: number;
  petsCount?: number;
}

const ShopErrorModal = ({
  history,
  selectedListing,
  fromDate,
  untilDate,
  adultsCount,
  childrenCount,
  petsCount,
}: ShopErrorModalProps) => {
  const { matchesMobile } = useDeviceTypes();

  const redirectToAvailability = () => {
    goToVRAvailability({
      history,
      listing: selectedListing,
      fromDate: fromDate || null,
      untilDate: untilDate || null,
      adultsCount: adultsCount,
      childrenCount: childrenCount,
      petsCount: petsCount,
    });
  };

  const modalEventProperties: VacationRentalsAlertProperties = {
    lob: "vacation rentals",
    type: "vacation_rentals_shop_error",
    modal_type: "vacation_rentals_shop_error",
    screen: ModalScreens.VR_DETAILS,
    primary_button: constants.RETURN_TO_SEARCH_MODAL_PRIMARY_BUTTON_TEXT || "",
    secondary_button: "",
    category: ModalCategoryType.TROUBLE,
    home_id: selectedListing?.listingId.id,
    check_in_date: fromDate ? dayjs(fromDate).format("YYYY-MM-DD") : undefined,
    check_out_date: untilDate
      ? dayjs(untilDate).format("YYYY-MM-DD")
      : undefined,
    funnel: "vacation_rentals",
    step: "shop",
    modal_title: constants.VR_NOT_AVAILABLE_MODAL_TITLE,
    modal_subtitle: constants.RETURN_TO_SEARCH_MODAL_SECONDARY_TEXT,
    agent_subtitle: "",
    agent_title: "",
  };

  useEffect(() => {
    trackEvent({
      eventName: MODAL_ALERT,
      properties: modalEventProperties,
    });
  }, []);

  const buttons = [
    {
      buttonText: constants.RETURN_TO_SEARCH_MODAL_PRIMARY_BUTTON_TEXT,
      defaultStyle: "h4r-primary",
      onClick: () => {
        redirectToAvailability();
      },
    },
  ];

  return (
    <GenericInfoPopup
      buttons={buttons}
      className="booking-error-modal"
      image={<Icon className="error-icon" name={IconName.ErrorState} />}
      isMobile={matchesMobile}
      open={true}
      title={constants.VR_NOT_AVAILABLE_MODAL_TITLE}
      subtitle={constants.RETURN_TO_SEARCH_MODAL_SECONDARY_TEXT}
      zIndex={matchesMobile ? 1301 : 1300}
    />
  );
};

interface ShopListingDetailsProps {
  selectedListing: ListingSearchResult;
  history: H.History;
  isVRForPremiumCardHoldersEnabled: boolean;
  isTravelApp?: boolean;
  isXmodRedesign: boolean;
  largestValueAccount?: any;
}

const ShopListingDetails = (props: ShopListingDetailsProps) => {
  const {
    selectedListing,
    history,
    isVRForPremiumCardHoldersEnabled,
    isTravelApp,
    isXmodRedesign,
    largestValueAccount,
  } = props;

  const { matchesDesktop, matchesMobile } = useDeviceTypes();
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [isTabScrolling, setIsTabScrolling] = useState<boolean>(false);
  const [isVettedQualityVisible, setIsVettedQualityVisible] =
    useState<boolean>(true);

  const showReviews =
    selectedListing?.listing.reviews &&
    selectedListing?.listing.reviews.length > 0;

  const formatDescription = (desc: string | undefined) => {
    if (!desc) return "";
    return desc
      .replace(/\/n\s\/n/g, "<br/><br/>")
      .replace(/\/n/g, "<br/>")
      .replace(/\n/g, "<br/>");
  };

  const { listing } = selectedListing;
  const { content, hostInfo, terms, reviews } = listing;
  const { name, description, amenities, layout, starRating, userRating } =
    content;
  const formattedDescription = formatDescription(description);
  const { bedrooms, numberOfBedrooms, numberOfBathrooms, bathrooms } = layout;

  const { hostName, hostImageUrl } = hostInfo || {};

  const { exactCoordinates } = content.location;

  const listingGoogleCoordinates = new google.maps.LatLng({
    lat: exactCoordinates.lat,
    lng: exactCoordinates.lon,
  });

  let city =
    content?.location?.address?.ListingAddress !== ListingAddressEnum.Empty
      ? content?.location?.address?.city
      : undefined;

  let state =
    content?.location?.address?.ListingAddress !== ListingAddressEnum.Empty
      ? content?.location?.address?.state
      : undefined;

  let countryCode =
    content?.location?.address?.ListingAddress !== ListingAddressEnum.Empty
      ? content?.location?.address?.countryCode
      : undefined;

  const { importantInformation, maxOccupancy } = terms;

  const renderMaxOccupancyIconLabel = (maxOccupancy: number) => {
    return (
      maxOccupancy > 0 && (
        <Box display="flex" alignItems={"center"}>
          <Icon
            name={IconName.UserOutline}
            width="14px"
            height="14px"
            strokeWidth="0.5px"
            className="icon-stroke-dark"
          />
          <Typography className="icon-label icon-label-spacing">
            Up to {maxOccupancy} {pluralize(maxOccupancy, "guest", "guests")}
          </Typography>
        </Box>
      )
    );
  };

  const renderNumberOfBedroomsIconLabel = (numBedrooms: number) => {
    return (
      numBedrooms > 0 && (
        <Box display="flex" alignItems="center">
          <Icon
            name={IconName.BedOutlineIcon}
            width="16px"
            height="16px"
            className="icon-fill-dark"
          />

          <Typography className="icon-label icon-label-spacing">
            {numBedrooms} {pluralize(numBedrooms, "bedroom", "bedrooms")}
          </Typography>
        </Box>
      )
    );
  };

  const renderNumberOfBathroomsIconLabel = (numBathrooms: number) => {
    return (
      numBathrooms > 0 && (
        <Box display="flex" alignItems="center">
          <Icon
            name={IconName.BathTubOutlineIcon}
            width="16px"
            height="16px"
            className="icon-stroke-dark"
          />
          <Typography className="icon-label icon-spacing">
            {numBathrooms} {pluralize(numBathrooms, "bathroom", "bathrooms")}
          </Typography>
        </Box>
      )
    );
  };

  const renderAmenityIconLabel = (amenity: Amenity) => {
    return (
      <Box display="flex" alignItems="flex-start" key={amenity.kind}>
        <AmenityIcon amenity={amenity.kind} className="icon-fill-dark" />
        <Typography className="amenity-icon-spacing">
          {amenity.displayName}
        </Typography>
      </Box>
    );
  };

  /** Navbar */
  const tabs = [
    {
      label: "Gallery",
      onClick: () => scrollToSection("gallery"),
    },

    { label: "About", onClick: () => scrollToSection("about") },
    {
      label: "Amenities",
      onClick: () => scrollToSection("amenities"),
    },
    ...(showReviews && reviews && reviews.length > 0
      ? [
          {
            label: "Reviews",
            onClick: () => scrollToSection("reviews"),
          },
        ]
      : []),
    {
      label: "Location",
      onClick: () => scrollToSection("location"),
    },
    {
      label: "House rules",
      onClick: () => scrollToSection("house-rules"),
    },
  ];

  const scrollToSection = (section: string) => {
    const elem = window.document.getElementById(section);
    if (elem) {
      setIsTabScrolling(true);

      const offset = 50 + 80 + 60 + 30; // 50 (banner) + 80 (toolbar) + 60 (navigation) + 30 (padding)
      const y = elem.getBoundingClientRect().top + window.scrollY - offset;

      window.scrollTo({ top: y, behavior: "smooth" });
      setTimeout(() => {
        setIsTabScrolling(false);
      }, 500);
    }
  };

  const [gallerySectionRef, gallerySectionInView] = useInView({
    threshold: matchesMobile ? 0.5 : 1,
    rootMargin: matchesMobile ? "0px 0px -100px 0px" : "0px 0px 500px 0px",
  });
  const [aboutSectionRef, aboutSectionInView] = useInView({
    threshold: matchesMobile ? 0.3 : 1,
    rootMargin: matchesMobile ? "0px 0px -50px 0px" : "0px 0px 500px 0px",
  });
  const [amenitiesSectionRef, amenitiesSectionInView] = useInView({
    threshold: 0.8,
  });
  const [reviewsSectionRef, reviewsSectionInView] = useInView({
    threshold: 0.8,
  });
  const [locationSectionRef, locationSectionInView] = useInView({
    threshold: 0.8,
  });
  const [houseRulesSectionRef, houseRulesSectionInView] = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (!isTabScrolling) {
      let tabIndex = 0;

      if (gallerySectionInView) {
        tabIndex = 0;
      } else if (aboutSectionInView) {
        tabIndex = 1;
      } else if (amenitiesSectionInView) {
        tabIndex = 2;
      } else if (reviewsSectionInView && showReviews) {
        tabIndex = 3;
      } else if (locationSectionInView) {
        tabIndex = showReviews ? 4 : 3;
      } else if (houseRulesSectionInView) {
        tabIndex = showReviews ? 5 : 4;
      }

      setSelectedTabIndex(tabIndex);
    }
  }, [
    gallerySectionInView,
    aboutSectionInView,
    amenitiesSectionInView,
    reviewsSectionInView,
    locationSectionInView,
    houseRulesSectionInView,
    showReviews,
    importantInformation,
  ]);

  /** Sections */

  const renderAbout = (isMobile: boolean) => {
    const premiumAmenities = amenities.filter((x) =>
      constants.PREMIUM_AMENITIES.includes(x.kind)
    );

    return (
      <div id="about" ref={aboutSectionRef}>
        <Box
          className={clsx("section", {
            mobile: isMobile,
          })}
        >
          {isMobile && (
            <HomeSummary
              paddingBottom="15px"
              paddingX="15px"
              city={city}
              starRating={starRating}
              userRating={userRating?.score}
            />
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            className={clsx("vr-name-container", {
              desktop: !isMobile,
            })}
          >
            <Typography className="vr-name-title" variant="h2">
              {name}
            </Typography>
            {!isMobile && userRating && userRating?.score && (
              <Box display="flex" alignItems="baseline">
                <Box display="flex" alignItems="center">
                  <Typography className="user-rating-text">
                    {getUserRatingQualifier(userRating?.score)}
                  </Typography>
                  <ReviewIconTooltip />
                  <ReviewScore score={userRating?.score} size="lg" />
                </Box>
              </Box>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            flexWrap="wrap"
            paddingBottom="20px"
            className={clsx("bedrooms-bathrooms-container", {
              desktop: !isMobile,
            })}
          >
            {renderMaxOccupancyIconLabel(maxOccupancy)}
            {renderNumberOfBedroomsIconLabel(numberOfBedrooms)}
            {renderNumberOfBathroomsIconLabel(numberOfBathrooms)}
          </Box>
          {isMobile && (
            <>
              <Box className="mobile-listing-panel-container">
                <Box display="flex" flexDirection="column" marginBottom="10px">
                  <MobileListingTotalPanel />
                </Box>
              </Box>
            </>
          )}
          <Box
            className={clsx("about-divider-container", {
              desktop: !isMobile,
            })}
          >
            <Divider className="divider-margin" />
          </Box>
          {premiumAmenities.length > 0 && (
            <>
              <Typography
                variant="h3"
                className={clsx("premium-amenities-title", {
                  mobile: isMobile,
                })}
              >
                {constants.PREMIUM_AMENITIES_TITLE}
              </Typography>
              <Box
                className={clsx("premium-amenities-grid", {
                  mobile: isMobile,
                })}
              >
                {premiumAmenities
                  .slice(0, 5)
                  .map((y) => renderAmenityIconLabel(y))}
              </Box>
            </>
          )}
          {description && (
            <Box
              className={clsx("description-container", {
                desktop: !isMobile,
              })}
            >
              <Typography className="description-title" variant="h4">
                Welcome to {name}
                {hostName
                  ? `, ${
                      /^[aeiouAEIOU]/.test(hostName) ? "an" : "a"
                    } ${hostName} home!`
                  : "!"}
              </Typography>
              <HotelDescription description={formattedDescription} />
            </Box>
          )}
          {isMobile && description && bedrooms.length > 0 && (
            <Box
              className={clsx("about-divider-container", {
                desktop: !isMobile,
              })}
            >
              <Divider className="divider-margin" />
            </Box>
          )}
          {bedrooms.length > 0 && (
            <>
              <Box
                className={clsx("rooms-and-beds-title-container", {
                  desktop: !isMobile,
                })}
              >
                <Typography className="layout-section-title" variant="h2">
                  {constants.ROOMS_AND_BED_TITLE}
                </Typography>
              </Box>
              <Box
                className={clsx("rooms-and-beds-container", {
                  desktop: !isMobile,
                })}
              >
                <RoomsAndBeds
                  bedrooms={bedrooms}
                  bathrooms={bathrooms}
                  className="vacation-rental-rooms-and-beds"
                  isMobile={matchesMobile}
                />
              </Box>
            </>
          )}
          {isMobile && bedrooms.length > 0 && hostName && (
            <Box
              className={clsx("about-divider-container", {
                desktop: !isMobile,
              })}
            >
              <Divider className="divider-margin" />
            </Box>
          )}
          {hostName && (
            <Box
              className={clsx("host-section-container", {
                desktop: !isMobile,
              })}
            >
              {!isMobile && <Divider className="divider-margin" />}
              <Box
                display="flex"
                alignItems="center"
                className="host-container"
              >
                {hostImageUrl && (
                  <img
                    src={hostImageUrl}
                    alt={hostName || "Host"}
                    className="host-image"
                  />
                )}
                <Typography className="host-title" variant="h2">
                  {hostName ? `Hosted by ${hostName}` : "Your host"}
                </Typography>
              </Box>
              <Typography variant="body1" className="host-description">
                {hostInfo?.hostDescription}
              </Typography>
            </Box>
          )}
        </Box>
      </div>
    );
  };

  const renderAmenities = () => {
    return (
      <div id="amenities" ref={amenitiesSectionRef}>
        <Box className="section">
          <Typography className="section-title" variant="h2">
            {constants.AMENITIES_TITLE}
          </Typography>
          <CategorizedAmenities
            amenities={amenities}
            className="vr-categorized-amenities"
          />
        </Box>
      </div>
    );
  };

  const renderReviews = () => {
    return (
      showReviews &&
      reviews &&
      reviews.length > 0 && (
        <div id="reviews" ref={reviewsSectionRef}>
          <Box className="section" position="relative">
            <Typography className="section-title" variant="h2">
              {constants.REVIEWS_TITLE}
            </Typography>
            <VacationRentalReviewsSection
              score={userRating?.score}
              reviews={reviews}
              isXmodRedesign={isXmodRedesign}
            />
          </Box>
        </div>
      )
    );
  };

  const renderLocation = () => {
    return (
      <div id="location" ref={locationSectionRef}>
        <Box className="section" position="relative" zIndex={0}>
          <Typography className="section-title" variant="h2">
            {constants.LOCATION_TITLE}
          </Typography>
          <VRMap
            listingName={name}
            location={content.location}
            coordinates={listingGoogleCoordinates}
            matchesMobile={matchesMobile}
            isXmodRedesign={isXmodRedesign}
            subtitle={[city, state, countryCode].filter(Boolean).join(", ")}
            subtitleClassName="location-subtitle"
          />
        </Box>
      </div>
    );
  };

  const renderHouseRules = () => {
    return (
      <div id="house-rules" ref={houseRulesSectionRef}>
        <VRHouseRules
          selectedListing={selectedListing}
          isMobile={matchesMobile}
        />
      </div>
    );
  };

  const renderImportantInformation = () => {
    if (!importantInformation) return null;

    const formattedImportantInfo = formatDescription(importantInformation);

    return (
      <div id="important-information">
        <Box className="section">
          <Typography className="section-title" variant="h2">
            {constants.IMPORTANT_INFORMATION_TITLE}
          </Typography>
          <HotelDescription description={formattedImportantInfo} />
        </Box>
      </div>
    );
  };

  const renderVettedForQuality = () => {
    if (!isVettedQualityVisible) return null;

    return (
      <Box
        className={clsx("vetted-quality-section", {
          "lifestyle-collection":
            selectedListing?.listing.listingCollection ===
            ListingCollectionEnum.Lifestyle,
          "premier-collection":
            selectedListing?.listing.listingCollection ===
            ListingCollectionEnum.Premier,
        })}
      >
        <Box className="vetted-quality-header">
          <Typography variant="h3" className="vetted-quality-title">
            {constants.VETTED_TITLE}
          </Typography>
          <CloseButtonIcon
            onClick={() => setIsVettedQualityVisible(false)}
            className="vetted-quality-close"
          />
        </Box>
        <Typography variant="body2" className="vetted-quality-text">
          {constants.VETTED_TEXT}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      className={clsx("vacation-rental-shop-refresh main-container", {
        "travel-app": isTravelApp,
        xmod: true,
      })}
    >
      {/* Mobile */}
      {matchesMobile && (
        <Box className="shop-mobile" display="flex" flexDirection="column">
          <MobileShopHeader
            isVRForPremiumCardHoldersEnabled={isVRForPremiumCardHoldersEnabled}
            isVR
          />
          <Box className="mobile-rewards">
            <ConnectedRewardsAccountSelection />
          </Box>
          <PageTabNavigations
            className={clsx("page-navigation vr-shop-navigation", {
              "lifestyle-collection":
                selectedListing?.listing.listingCollection ===
                ListingCollectionEnum.Lifestyle,
              "premier-collection":
                selectedListing?.listing.listingCollection ===
                ListingCollectionEnum.Premier,
            })}
            navItems={tabs}
            selectedTab={selectedTabIndex}
            rightContent={<></>}
          />
          <Box id="gallery">
            <HotelCarousel media={selectedListing?.listing.content.media} />
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            padding="20px"
            className="mobile-content"
          >
            {renderVettedForQuality()}
            {renderAbout(true)}
            {renderAmenities()}
            {renderReviews()}
            {renderLocation()}
            {renderHouseRules()}
            {renderImportantInformation()}
          </Box>
          <Box
            position="fixed"
            bottom="0px"
            boxShadow="0px -2px 10px rgba(0, 0, 0, 0.2)"
            width="100%"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <MobileListingTotalPanel />
          </Box>
        </Box>
      )}

      {/* Desktop */}
      {matchesDesktop && (
        <Box className="shop-desktop" display="flex" flexDirection="column">
          <ConnectedDesktopRewardsHeader isVR={true} />
          <div ref={gallerySectionRef} id="gallery">
            <Box paddingX="120px">
              <DesktopHotelPhotoModal />
            </Box>
          </div>
          <PageTabNavigations
            className={clsx("page-navigation", {
              "lifestyle-collection":
                selectedListing?.listing.listingCollection ===
                ListingCollectionEnum.Lifestyle,
              "premier-collection":
                selectedListing?.listing.listingCollection ===
                ListingCollectionEnum.Premier,
            })}
            navItems={tabs}
            selectedTab={selectedTabIndex}
            rightContent={<></>}
          />
          <Box
            display="flex"
            flexDirection="row"
            className="desktop-content"
            paddingX="120px"
            paddingY="30px"
          >
            <Box display="flex" flexDirection="column">
              {renderVettedForQuality()}
              {renderAbout(false)}
              {renderAmenities()}
              {renderReviews()}
              {renderLocation()}
              {renderHouseRules()}
              {renderImportantInformation()}
            </Box>

            <Box
              position="sticky"
              minWidth="450px"
              maxWidth="500px"
              width="100%"
              height="100%"
              className="desktop-summary-box"
              display="flex"
              flexDirection="column"
              border="solid 0.5px #CECFCD"
              borderRadius="8px"
            >
              <DesktopListingSummaryBox
                isVRForPremiumCardHoldersEnabled={
                  isVRForPremiumCardHoldersEnabled
                }
                updatedBenefits={true}
                largestValueAccount={largestValueAccount}
              />
            </Box>
          </Box>
        </Box>
      )}

      <ConnectedWishlistManagementModal history={history} entryType="vr_shop" />
      <WishlistSnackbar />
    </Box>
  );
};
